import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Fade,
  Slide,
  Container,
  Paper,
  Divider,
  Chip,
  Avatar,
  Grid,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Login as LoginIcon,
  Diamond,
  Business,
  Security,
  Speed,
  TrendingUp,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '../redux/actions/authActions';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [animationKey, setAnimationKey] = useState(0);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAnimationKey(prev => prev + 1);
    const result = await dispatch(login(formData));
    if (result.success) {
      navigate('/dashboard');
    }
  };

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleFieldFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleFieldBlur = () => {
    setFocusedField(null);
  };

  // Demo credentials for quick login
  const demoCredentials = [
    { role: 'Admin', username: 'admin', password: 'admin123', color: '#f44336' },
    { role: 'Cashier', username: 'cashier', password: 'cashier123', color: '#2196f3' },
    { role: 'Staff', username: 'staff', password: 'staff123', color: '#4caf50' },
  ];

  const handleDemoLogin = (credentials) => {
    setFormData({
      username: credentials.username,
      password: credentials.password,
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)
          `,
          animation: 'float 6s ease-in-out infinite',
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
          '50%': { transform: 'translateY(-20px) rotate(1deg)' },
        },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          {/* Left Side - Branding & Features */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' }, pr: { md: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      mr: 2,
                      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                    }}
                  >
                    <Diamond sx={{ fontSize: 40, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h2"
                      component="h1"
                      sx={{
                        fontWeight: 'bold',
                        color: 'white',
                        textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                      }}
                    >
                      JBilling
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 300,
                        fontSize: { xs: '1.1rem', md: '1.3rem' },
                      }}
                    >
                      Jewellery Shop Management System
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    mb: 4,
                    fontWeight: 300,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                  }}
                >
                  Streamline Your Business Operations
                </Typography>

                <Grid container spacing={3}>
                  {[
                    { icon: <Business />, title: 'Inventory Management', desc: 'Track your jewellery stock efficiently' },
                    { icon: <TrendingUp />, title: 'Sales Analytics', desc: 'Monitor your business performance' },
                    { icon: <Security />, title: 'Secure Billing', desc: 'Safe and reliable payment processing' },
                    { icon: <Speed />, title: 'Fast Operations', desc: 'Quick and easy daily transactions' },
                  ].map((feature, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Fade in timeout={1000 + index * 200}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 2,
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: 2,
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.15)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          <Avatar
                            sx={{
                              background: 'rgba(255, 255, 255, 0.2)',
                              mr: 2,
                              width: 40,
                              height: 40,
                            }}
                          >
                            {feature.icon}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                              {feature.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                              {feature.desc}
                            </Typography>
                          </Box>
                        </Box>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Fade>
          </Grid>

          {/* Right Side - Login Form */}
          <Grid item xs={12} md={6}>
            <Slide direction="left" in timeout={1000}>
              <Card
                sx={{
                  maxWidth: 480,
                  width: '100%',
                  mx: 'auto',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    p: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                    Welcome Back
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    Sign in to your account
                  </Typography>
                </Box>

                <CardContent sx={{ p: 4 }}>
                  {error && (
                    <Fade in>
                      <Alert 
                        severity="error" 
                        sx={{ 
                          mb: 3, 
                          borderRadius: 2,
                          '& .MuiAlert-message': {
                            width: '100%',
                          }
                        }}
                      >
                        {error}
                      </Alert>
                    </Fade>
                  )}

                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      onFocus={() => handleFieldFocus('username')}
                      onBlur={handleFieldBlur}
                      margin="normal"
                      required
                      autoComplete="username"
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person 
                              sx={{ 
                                color: focusedField === 'username' ? '#667eea' : 'action.active',
                                transition: 'color 0.3s ease'
                              }} 
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea',
                            },
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea',
                              borderWidth: 2,
                            },
                          },
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => handleFieldFocus('password')}
                      onBlur={handleFieldBlur}
                      margin="normal"
                      required
                      autoComplete="current-password"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock 
                              sx={{ 
                                color: focusedField === 'password' ? '#667eea' : 'action.active',
                                transition: 'color 0.3s ease'
                              }} 
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handlePasswordVisibility}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea',
                            },
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea',
                              borderWidth: 2,
                            },
                          },
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                      sx={{
                        mt: 4,
                        mb: 3,
                        py: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                        },
                        '&:disabled': {
                          background: 'rgba(0, 0, 0, 0.12)',
                          color: 'rgba(0, 0, 0, 0.26)',
                        },
                      }}
                    >
                      {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                  </form>

                  <Divider sx={{ my: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Quick Demo Access
                    </Typography>
                  </Divider>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {demoCredentials.map((cred, index) => (
                      <Fade in timeout={1200 + index * 200} key={cred.role}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                            border: '1px solid rgba(102, 126, 234, 0.1)',
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.15)',
                            },
                          }}
                          onClick={() => handleDemoLogin(cred)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Chip
                                label={cred.role}
                                size="small"
                                sx={{
                                  background: cred.color,
                                  color: 'white',
                                  fontWeight: 600,
                                  mr: 2,
                                }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {cred.username} / {cred.password}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Click to use
                            </Typography>
                          </Box>
                        </Paper>
                      </Fade>
                    ))}
                  </Box>

                  {/* Footer - Powered By and Copyright */}
                  <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        mb: 1,
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}
                    >
                      Powered By <strong style={{ color: '#667eea' }}>Appsinlive</strong>
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.75rem',
                        opacity: 0.7
                      }}
                    >
                      Copyright © 2025. All rights reserved.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Slide>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Login;
